import { sbEditable } from '@storyblok/storyblok-editable';
import bowser from 'bowser';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SolutionCardProps } from './SolutionCard.data';
import styles from './SolutionCard.module.scss';
import {
  onHover,
  scaleImage,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './SolutionCard.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Image from '../../../atoms/image/Image';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import { cleanMediaQueries } from '../../../../hooks/useDeviceStateTracker';
import { isTouch, useIsDesktopDevice } from '../../../../hooks/useIsDesktopDevice';
import PrimaryLink from '../../../atoms/primary-link/PrimaryLink';
import { useGlobalStore } from '../../../../stores/global';
import type { CtaRefType } from '../../../../util/cta/cta.data';
import type { ImageRef } from '../../../atoms/image/Image.data';
import { getLink } from '../../../../util/url.utils';
import { useRouter } from 'next/router';

export default function SolutionCard({
  isVisible,
  className,
  blok,
  ref,
}: SolutionCardProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const globalStore = useGlobalStore();
  const [isHover, setIsHover] = useState(false);
  const linkRef = useRef<CtaRefType>(null);
  const imageRef = useRef<ImageRef>(null);
  const backgroundHoverRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const isDesktopDevice = useIsDesktopDevice(false);
  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const link = getLink(blok?.link, router.locale);
  const onMouseEnter = useCallback(() => {
    if (link && isDesktopDevice) {
      setIsHover(true);
    }
  }, [isDesktopDevice, link]);

  const onMouseLeave = useCallback(() => {
    if (isDesktopDevice) {
      setIsHover(false);
    }
  }, [isDesktopDevice]);

  const onClick = useCallback(() => {
    if (link && linkRef?.current) {
      linkRef.current.click();
    }
  }, [link]);

  useEffect(() => {
    onHover(backgroundHoverRef, isHover);
    scaleImage(imageRef, isHover);
  }, [isHover]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.solutionCard, className, {
        [styles.isButton]: link,
      })}
      {...sbEditable(blok)}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className={styles.imageContainer}>
        <Image
          ref={imageRef}
          src={blok.image.filename}
          srcDesktop={blok.image.filename}
          layout="fill"
          className={classNames(styles.image, 'abs-fill')}
          sizes={`${cleanMediaQueries.MIN_768} 384px, ${cleanMediaQueries.MIN_1920} 640px, 100vw`}
          alt={blok.image.alt}
          innerTransition={{
            type: 'parallax',
            config: {
              speed: -2,
            },
          }}
        />
      </div>
      <div className={styles.textContainer}>
        <div ref={backgroundHoverRef} className={classNames(styles.backgroundHover, 'abs-fill')} />
        <div className={classNames(styles.titleHolder)}>
          <Heading
            as="h4"
            size="label-large"
            disableTriggerInOnScroll={false}
            className={styles.title}
            transitionInType={!isDesktopDevice ? 'none' : 'splitLineRotateIn'}
          >
            {blok.title}
          </Heading>
        </div>
        <div className={classNames(styles.descriptionHolder)}>
          <Text
            as="p"
            size="body-standard"
            transitionInType={!isDesktopDevice || bowser.safari ? 'none' : 'splitLineRotateIn'}
            disableTriggerInOnScroll={false}
            className={classNames(styles.description)}
          >
            {blok.description}
          </Text>

          {link && (
            <PrimaryLink
              ref={linkRef}
              textVariant="button-label-small"
              className={classNames(styles.link)}
              link={{ href: link }}
              label={globalStore.globalSettings.view_solution}
              disableTriggerInOnScroll={false}
              transitionInType={isTouch ? 'none' : 'splitLineRotateIn'}
              isActive={isHover}
            />
          )}
        </div>
      </div>
    </div>
  );
}
